/**
 * PageLayout Component
 *
 * Main page layout with header, sidebar, footer and content area.
 * O header é fixo no topo e o conteúdo passa por trás dele.
 * Suporta banner de impersonate quando admin está acessando como outro usuário.
 *
 * @since 2025-12-16
 * @epic INIT-0045-03, INIT-0046
 */

import { ReactNode } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import { ImpersonateBanner } from './ImpersonateBanner';
import { useUIStore } from '@/store';
import { useImpersonateStore } from '@/store/impersonateStore';
import { useTheme } from '@/theme';
import { useAuth } from '@/contexts/AuthContext';
import clsx from 'clsx';

interface PageLayoutProps {
  children: ReactNode;
  showSidebar?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  fullWidth?: boolean;
  /** Remove o padding interno do conteúdo (para páginas que precisam de controle total) */
  noPadding?: boolean;
}

export function PageLayout({
  children,
  showSidebar = false,
  showHeader = true,
  showFooter = true,
  fullWidth = true,
  noPadding = false,
}: PageLayoutProps) {
  const { sidebarOpen, sidebarCollapsed } = useUIStore();
  const { isImpersonating } = useImpersonateStore();
  const { theme } = useTheme();

  // Em mobile (<sm), sem margem pois o sidebar fica como overlay
  const contentMargin = showSidebar && sidebarOpen
    ? sidebarCollapsed ? 'sm:ml-16' : 'sm:ml-72'
    : '';

  // Altura do banner de impersonate (48px)
  const impersonateBannerHeight = isImpersonating ? 48 : 0;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Banner de Impersonate (fixo acima do header) */}
      <ImpersonateBanner />

      {/* Header fixo no topo (abaixo do banner se impersonando) */}
      {showHeader && (
        <div
          className="fixed left-0 right-0 z-50"
          style={{ top: impersonateBannerHeight }}
        >
          <Header />
        </div>
      )}
      {showSidebar && <Sidebar />}

      {/* Conteúdo principal com padding-top para compensar header fixo + banner */}
      <main
        className={clsx(
          'flex-1 transition-all duration-300',
          contentMargin
        )}
        style={{
          backgroundColor: 'hsl(var(--background))',
          paddingTop: showHeader ? `${64 + impersonateBannerHeight}px` : `${impersonateBannerHeight}px`,
        }}
      >
        <div
          className={clsx(
            !fullWidth && 'max-w-7xl mx-auto',
            !noPadding && 'px-4 py-4'
          )}
        >
          {children}
        </div>
      </main>

      {/* Footer */}
      {showFooter && <Footer />}
    </div>
  );
}

// Simple layout without sidebar (para páginas de autenticação)
interface SimpleLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
}

export function SimpleLayout({ children, showHeader = false }: SimpleLayoutProps) {
  return (
    <div className="min-h-screen bg-background">
      {showHeader && <Header />}
      <main className={showHeader ? 'pt-14' : ''}>{children}</main>
    </div>
  );
}

// AuthLayout - Layout específico para páginas de login/registro
interface AuthLayoutProps {
  children: ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      {children}
    </div>
  );
}

// =============================================================================
// CampaignLayout - Layout para páginas públicas de campanha (termo de adesão)
// =============================================================================

export interface CampaignTheme {
  logoUrl?: string;
  backgroundUrl?: string;
  backgroundColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

interface CampaignLayoutProps {
  children: ReactNode;
  campaignName?: string;
  campaignTheme?: CampaignTheme;
  showFooter?: boolean;
}

export function CampaignLayout({
  children,
  campaignName = 'Campanha',
  campaignTheme,
  showFooter = true,
}: CampaignLayoutProps) {
  const bgColor = campaignTheme?.backgroundColor || 'var(--brand-primary, #1a365d)';
  const primaryColor = campaignTheme?.primaryColor || 'var(--brand-primary-hover, #3182ce)';

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: bgColor,
        backgroundImage: campaignTheme?.backgroundUrl
          ? `url(${campaignTheme.backgroundUrl})`
          : `linear-gradient(135deg, ${bgColor} 0%, ${primaryColor} 100%)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Header da Campanha */}
      <header className="py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          {campaignTheme?.logoUrl ? (
            <img
              src={campaignTheme.logoUrl}
              alt={campaignName}
              className="h-16 md:h-20 object-contain"
            />
          ) : (
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
              {campaignName}
            </h1>
          )}
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="flex-1 flex items-start justify-center px-4 py-8">
        <div className="w-full max-w-xl">
          {children}
        </div>
      </main>

      {/* Footer da Campanha */}
      {showFooter && (
        <footer className="py-4 px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <p className="text-white/60 text-sm">
              Powered by Trade Marketing Force
            </p>
            <div className="mt-2 flex justify-center gap-4 text-xs text-white/40">
              <a href="#regulamento" className="hover:text-white/80 transition-colors">
                Regulamento
              </a>
              <span>|</span>
              <a href="#privacidade" className="hover:text-white/80 transition-colors">
                Política de Privacidade
              </a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

// =============================================================================
// PublicPortalLayout - Layout para portal de conteúdo público (estilo Globo.com)
// =============================================================================

interface PortalCategory {
  slug: string;
  name: string;
  url: string;
}

interface PublicPortalLayoutProps {
  children: ReactNode;
  siteName?: string;
  logoUrl?: string;
  categories?: PortalCategory[];
  showSearch?: boolean;
}

export function PublicPortalLayout({
  children,
  siteName = 'Portal de Conteúdo',
  logoUrl,
  categories = [],
  showSearch = true,
}: PublicPortalLayoutProps) {
  const { isAuthenticated, user } = useAuth();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header do Portal */}
      <header className="bg-card shadow-sm sticky top-0 z-50">
        {/* Top Bar */}
        <div className="border-b border-border">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <a href="/portal" className="flex items-center gap-2">
                {logoUrl ? (
                  <img src={logoUrl} alt={siteName} className="h-8 object-contain" />
                ) : (
                  <span className="text-xl font-bold text-foreground">
                    {siteName}
                  </span>
                )}
              </a>

              {/* Busca e Login */}
              <div className="flex items-center gap-4">
                {showSearch && (
                  <div className="hidden md:block">
                    <input
                      type="search"
                      placeholder="Buscar..."
                      className="w-64 px-4 py-2 rounded-full border border-border bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                {isAuthenticated ? (
                  <a
                    href="/"
                    className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground"
                  >
                    {user?.name || 'Minha Conta'}
                  </a>
                ) : (
                  <a
                    href="/login"
                    className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground"
                  >
                    Entrar
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        {categories.length > 0 && (
          <nav className="max-w-7xl mx-auto px-4">
            <ul className="flex items-center gap-1 py-2 overflow-x-auto">
              <li>
                <a
                  href="/portal"
                  className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-muted transition-colors"
                >
                  Início
                </a>
              </li>
              {categories.map((category) => (
                <li key={category.slug}>
                  <a
                    href={category.url}
                    className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-muted transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      {/* Conteúdo Principal */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer do Portal */}
      <footer className="bg-gray-800 dark:bg-black/20 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Sobre */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Sobre</h3>
              <p className="text-muted-foreground text-sm">
                Portal de conteúdo exclusivo para nossos parceiros e clientes.
              </p>
            </div>

            {/* Links Rápidos */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Links Rápidos</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/portal" className="hover:text-white">Início</a></li>
                <li><a href="/portal/search" className="hover:text-white">Buscar</a></li>
                <li><a href="/login" className="hover:text-white">Área Restrita</a></li>
              </ul>
            </div>

            {/* Categorias */}
            {categories.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Categorias</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {categories.slice(0, 5).map((cat) => (
                    <li key={cat.slug}>
                      <a href={cat.url} className="hover:text-white">{cat.name}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contato */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contato</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>contato@trademarketingforce.com</li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Trade Marketing Force. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
